<template>
  <div style="width:calc(100% - 80px); margin-left: 40px; margin-right: 40px;">
    <div
      v-for="(item, index) in items"
      v-show="showRejectedBudget || item.summary.requestStatus !== 'rejected'"
      :key="index"
      style="width:100%; border: 1px solid #848484;"
    >
      <RowTitle
        :item="item"
        :index="index"
        :readonly="readonly"
        :resubmittable="resubmittable"
        :is-open="isOpen(index)"
        :can-open="sortedCampaignBudgets(item).length > 0"
        :error-message="errorMessageOnTitleBar(item)"
        @open="openInsertionOrderBudget(index)"
        @closeAndEditInsertionOrder="$emit('closeAndEditInsertionOrder')"
        @clickAddButton="onClickAddButton(item, index)"
        class="title-row"
      />
      <RowInsertionOrderPeriod
        :item="item"
        :index="index"
        :display-start-date="displayStartDate"
        :display-end-date="displayEndDate"
        class="box-row"
      />
      <div
        v-for="(campaignBudget, dIndex) in sortedCampaignBudgets(item)"
        :key="dIndex"
      >
        <v-tooltip bottom>
          <template slot="activator">
            <div
              slot="activator"
              @click="openInsertionOrderBudget(index)"
              class="box-row budget-row"
            >
              <v-layout style="min-height: 28px; position:relative">
                <span class="budget-title">
                  キャンペーン予算{{ dIndex + 1 }}
                </span>
                <DateBar
                  :display-start-date="displayStartDate"
                  :display-end-date="displayEndDate"
                  :start-date="item.summary.startTime"
                  :end-date="item.summary.endTime"
                  style="background-color: #BDBDBD; min-height: 28px; width:100%; position:relative;"
                >
                  <DateRange
                    :duplicate-ranges="overlappingPeriodsList(item)[dIndex]"
                    :deliveries="item.campaignBudgets"
                    :delivery="campaignBudget"
                    :start-date="item.summary.startTime"
                    :end-date="item.summary.endTime"
                    :display-start-date="displayStartDate"
                    :display-end-date="displayEndDate"
                  />
                </DateBar>
              </v-layout>
            </div>
          </template>
          <span>
            <AppCurrency :value="campaignBudget.budget" />:
            <AppPeriod
              :start="campaignBudget.startTime"
              :end="campaignBudget.endTime"
            />
          </span>
        </v-tooltip>
        <v-slide-y-transition>
          <v-layout v-if="isOpen(index)" class="box-row">
            <v-flex xs10>
              <v-layout column>
                <v-flex>
                  <DateTimeRangePicker
                    v-if="!readonly"
                    :disabled="!item.canEditCampaignBudgets"
                    :disabled-start="!campaignBudget.canEditStartTime"
                    :disabled-end="!campaignBudget.canEditEndTime"
                    :campaign-budget="campaignBudget"
                    :io-start-date="item.startTimeSelectable"
                    :io-end-date="item.startEndSelectable"
                    label="配信日"
                    class="pt-3"
                  />
                  <AppForm
                    v-if="readonly"
                    :class="{
                      'grey--text': isRejected(item),
                    }"
                    label="配信日"
                  >
                    <span class="title">
                      <AppDateTime :value="campaignBudget.startTime" />〜
                      <AppDateTime :value="campaignBudget.endTime" />
                    </span>
                  </AppForm>
                  <RowBudget
                    v-if="!readonly"
                    :disabled="!item.canEditCampaignBudgets"
                    :message="errorMessage(item, dIndex)"
                    :show-help="item.canShowHelpToChangeRequest"
                    :delivery="campaignBudget"
                    :readonly="readonly"
                    class="pt-3"
                  />
                  <AppForm
                    v-if="readonly"
                    :class="{
                      'grey--text': isRejected(item),
                    }"
                    label="上限予算"
                  >
                    <AppCurrency :value="campaignBudget.budget" class="title" />
                  </AppForm>
                  <v-slide-y-transition>
                    <v-layout v-if="setPace" class="pt-3">
                      <InventoryPace
                        :disabled="!item.canEditCampaignBudgets"
                        :percentage="campaignBudget.evenPacePercentage"
                        :budget="campaignBudget.dailyLimitBudget"
                        :type="campaignBudget.pacingType"
                        @setType="campaignBudget.pacingType = $event"
                        @setPercentage="
                          campaignBudget.evenPacePercentage = $event
                        "
                        @setBudget="
                          campaignBudget.dailyLimitBudget = Number($event)
                        "
                      />
                    </v-layout>
                  </v-slide-y-transition>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs2>
              <v-layout align-center justify-center fill-height>
                <v-btn
                  v-if="
                    item.canEditCampaignBudgets &&
                      !readonly &&
                      campaignBudget.canDelete
                  "
                  @click.stop="onClickDeleteButton(campaignBudget)"
                  icon
                  small
                  color="error"
                >
                  <v-icon>close</v-icon>
                </v-btn>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-slide-y-transition>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import sortBy from 'lodash/sortBy'
import RowTitle from './CampaignModalPageBudgetListRowTitle'
import RowInsertionOrderPeriod from './CampaignModalPageBudgetListRowInsertionOrderPeriod'
import RowBudget from './CampaignModalPageBudgetListRowBudget'
import InventoryPace from '@/components/Advertiser/Operation/IO/CampaignModalPageBudgetInventoryPace'
import DateTimeRangePicker from '@/components/Advertiser/Operation/IO/Form/FormDateTimeRange'
import DateBar from './CampaignModalPageBudgetListDateBar'
import DateRange from './CampaignModalPageBudgetListDateRange'
import { Form } from '@/models/CampaignOperation'

export default {
  components: {
    RowTitle,
    RowInsertionOrderPeriod,
    RowBudget,
    InventoryPace,
    DateTimeRangePicker,
    DateBar,
    DateRange,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    setPace: {
      type: Boolean,
      default: false,
    },
    showRejectedBudget: {
      type: Boolean,
      deafult: false,
    },
    displayStartDate: {
      type: String,
      default: '',
    },
    displayEndDate: {
      type: String,
      default: '',
    },
    readonly: Boolean,
    resubmittable: Boolean,
  },
  data() {
    return {
      open: [],
    }
  },
  computed: {
    items() {
      this.form.insertionOrderBudgets.map(ioBudget => {
        if (ioBudget.latestRequest.requestStatus === 'rejected') {
          this.approvedBudgets(ioBudget)
        }
      })
      return sortBy(this.form.insertionOrderBudgets, iob =>
        dayjs(iob.summary.startTime).unix(),
      )
    },
  },
  created() {
    this.openDeliveringOrReadyBudget()
  },
  methods: {
    sortedCampaignBudgets(item) {
      return Form.getCampaignBudgets(
        this.form.campaign.mergedCampaignBudgets,
        item,
      )
    },
    onClickAddButton(insertionOrderBudget, index) {
      if (!this.isOpen(index)) this.openInsertionOrderBudget(index)
      this.$emit('clickAddButton', insertionOrderBudget)
    },
    onClickDeleteButton(campaignBudget) {
      this.form.campaign.deleteCampaignBudget(campaignBudget)
    },
    openInsertionOrderBudget(index) {
      const indexInOpen = this.open.indexOf(index)
      if (indexInOpen > -1) {
        this.open.splice(indexInOpen, 1)
      } else {
        this.open = this.open.concat(index)
      }
    },
    openDeliveringOrReadyBudget() {
      this.open = this.items
        .map((item, index) => [item, index])
        .filter(([item]) => !dayjs(item.summary.endTime).isBefore(dayjs()))
        .map(([, index]) => index)
    },
    isRejected(insertionOrderBudget) {
      return insertionOrderBudget.summary.requestStatus === 'rejected'
    },
    isOpen(index) {
      return this.open.includes(index)
    },
    campaignBudgets(item) {
      return Form.getCampaignBudgets(
        this.form.campaign.mergedCampaignBudgets,
        item,
      )
    },
    violations(item) {
      return Form.getInsertionOrderBudgetViolations(
        item,
        this.campaignBudgets(item),
      )
    },
    overlappingPeriodsList(item) {
      return Form.getOverlappingPeriodsList(this.campaignBudgets(item))
    },
    overlappingErrorMessages(item) {
      return this.violations(item).map(violation =>
        violation.overlapping.length > 0
          ? '配信期間が重複しないように修正してください。'
          : '',
      )
    },
    overBudgetErrorMessages(item) {
      return this.violations(item).map(violation =>
        violation.overBudget
          ? '案件予算を上限予算が超過しています。\n上限予算を修正してください。'
          : '',
      )
    },
    reversePeriodErrorMessages(item) {
      return this.violations(item).map(violation =>
        violation.reversePeriod
          ? '配信開始時刻が終了時刻より前になるように修正してください。'
          : '',
      )
    },
    rejectedWarningMessages(item) {
      return this.campaignBudgets(item).map(() =>
        item.summary.requestStatus === 'rejected'
          ? '案件予算が却下されたため、本期間は配信されません。\n案件予算を再申請してください。'
          : '',
      )
    },
    async approvedBudgets(item) {
      const ioBudgets = await this.form.insertionOrder.getBudgetList()
      const correspondingBudget = ioBudgets.find(
        ioBudget => ioBudget.latestRequest.id === item.latestRequest.id,
      )
      const isRequestsApproved = correspondingBudget.requests.some(
        request => request.requestStatus === 'approved',
      )
      if (isRequestsApproved) {
        item.latestRequest.requestStatus = 'approved'
      }
    },
    errorMessage(item, index) {
      return (
        this.rejectedWarningMessages(item)[index] ||
        this.overBudgetErrorMessages(item)[index] ||
        this.reversePeriodErrorMessages(item)[index] ||
        this.overlappingErrorMessages(item)[index]
      )
    },
    errorMessageOnTitleBar(item) {
      return this.sortedCampaignBudgets(item).length === 0
        ? 'キャンペーン予算未設定のため配信されません。'
        : ''
    },
  },
}
</script>

<style scoped>
.title-row {
  padding-left: 14px;
  min-height: 42px;
}
.box-row {
  padding-left: 14px;
  border-top: 1px solid #848484;
  min-height: 28px;
}
.budget-row {
  cursor: pointer;
  background-color: #efefef;
}
.spacer--text {
  display: inline-block;
  margin-right: 20px;
}
.budget-title {
  font-size: 10px;
  margin-top: 7px;
  width: 100px;
}
</style>
